/**
 * Folding the firewall checks and the call test into the one status the banner shows.
 *
 * The two halves answer different questions — can we reach the platform, and does a real call
 * through it sound acceptable — so the run is only as good as its weaker half.
 */

import type { OverallStatus } from './status'
import type { Verdict } from './calltest/types'

export type CallOutcome =
	| { kind: 'pending' }
	/** Provisioning, registration or the media setup never got as far as a call. */
	| { kind: 'failed-to-start' }
	/** A call was placed but never answered. */
	| { kind: 'not-connected' }
	| { kind: 'graded', verdict: Verdict }

const RANK: Record<OverallStatus, number> = { ok: 0, warn: 1, fail: 2, run: 3 }

/** Worst of two statuses, with `run` outranking everything: an unfinished run has no verdict. */
export function worstStatus(a: OverallStatus, b: OverallStatus): OverallStatus {
	return RANK[a] >= RANK[b] ? a : b
}

/**
 * `NOT_MEASURED` deliberately warns rather than passes. A call nobody could measure is not a
 * call that went well, and presenting it as green is the one error this page must not make.
 */
const VERDICT_STATUS: Record<Verdict, OverallStatus> = {
	OK: 'ok',
	DEGRADED: 'warn',
	NOT_MEASURED: 'warn',
	BAD: 'fail',
	NO_AUDIO: 'fail'
}

export function combineStatus(firewall: OverallStatus, call: CallOutcome): OverallStatus {
	return worstStatus(firewall, callStatus(call))
}

function callStatus(call: CallOutcome): OverallStatus {
	switch (call.kind) {
		case 'pending': return 'run'
		case 'failed-to-start': return 'fail'
		case 'not-connected': return 'fail'
		case 'graded': return VERDICT_STATUS[call.verdict]
	}
}
